import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { useAppDispatch } from 'src/store';
import { Status as StatusEnum } from 'src/ts/enums';

import { PlatformFeedbackCategory, PlatformFeedbackReviewOutcome } from '../api';
import { actions as myWorkActions, selectors as myWorkSelectors } from '../myWorkSlice';
import { reportPlatformFeedbackThunk } from '../services';

export type PlatformFeedbackReport = {
  category: PlatformFeedbackCategory;
  feedback: string;
  canCompleteReview: boolean;
  proofId?: string;
};

const REVIEW_OUTCOMES: PlatformFeedbackReviewOutcome[] = ['kept', 'released', 'already_released'];

export const useReportPlatformFeedback = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showSnackbar, VariantType } = useSnackbarProvider();
  const status = useSelector(myWorkSelectors.reportPlatformFeedback.status);

  // Resolves what the server did with the review, or null on any error.
  const onReportPlatformFeedback = async (
    entryId: string,
    report: PlatformFeedbackReport
  ): Promise<PlatformFeedbackReviewOutcome | null> => {
    try {
      const { review } = await dispatch(
        reportPlatformFeedbackThunk({
          platform_feedback: {
            product_area: 'manual_review',
            entry_id: entryId,
            category: report.category,
            feedback: report.feedback,
            can_complete_review: report.canCompleteReview,
            proof_id: report.proofId
          }
        })
      ).unwrap();

      if (!REVIEW_OUTCOMES.includes(review)) {
        throw new Error('Unexpected platform feedback response');
      }

      // The analyst stays on the review, so confirm the report here.
      if (review === 'kept') {
        showSnackbar(VariantType.success, 'Thanks. Your report was sent to the Snappt team.');
      }

      return review;
    } catch {
      showSnackbar(VariantType.error, 'Could not report the feedback. Please try again.');
      return null;
    }
  };

  // Stable across renders, because the modal's confirmation timer restarts
  // whenever this function changes.
  const onFinish = useCallback(
    (review: Exclude<PlatformFeedbackReviewOutcome, 'kept'>) => {
      navigate('/my-work');
      dispatch(
        review === 'released'
          ? myWorkActions.clearAssignmentAfterPlatformFeedback()
          : myWorkActions.setAssignedEntry(null)
      );
    },
    [navigate, dispatch]
  );

  return { onReportPlatformFeedback, onFinish, isLoading: status === StatusEnum.LOADING };
};
